import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AdminPanelSettings as AdminIcon,
  Work as StaffIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService } from '@services/sheltersService';

interface ShelterStaff {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'shelter_admin' | 'shelter_staff';
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin?: string;
}

const ShelterStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [staff, setStaff] = useState<ShelterStaff[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<ShelterStaff | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuStaffId, setMenuStaffId] = useState<string | null>(null);

  // Form states
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'shelter_staff' as 'shelter_admin' | 'shelter_staff'
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      const staffData = await shelterService.getShelterStaff(user.shelterId);
      setStaff(staffData || []);
    } catch (error) {
      console.error('Error loading staff:', error);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteStaff = async () => {
    try {
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      await shelterService.inviteShelterStaff(user.shelterId, {
        email: inviteForm.email,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        role: inviteForm.role
      });

      setInviteDialogOpen(false);
      setInviteForm({ email: '', firstName: '', lastName: '', role: 'shelter_staff' });
      loadStaff(); // Reload staff list
    } catch (error) {
      console.error('Error inviting staff:', error);
    }
  };

  const handleDeleteStaff = async (staffId: string) => {
    try {
      await shelterService.removeShelterStaff(staffId);
      setStaff(staff.filter(member => member.id !== staffId));
      setDeleteDialogOpen(false);
      setSelectedStaff(null);
    } catch (error) {
      console.error('Error deleting staff:', error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, staffId: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuStaffId(staffId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuStaffId(null);
  };

  const getRoleColor = (role: string) => {
    return role === 'shelter_admin' ? 'error' : 'primary';
  };

  const getRoleIcon = (role: string) => {
    return role === 'shelter_admin' ? <AdminIcon /> : <StaffIcon />;
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getStatusIcon = (status: string) => {
    return status === 'active' ? <ActiveIcon /> : <InactiveIcon />;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Management Staff
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionează echipa adapostului
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invită Membru
        </Button>
      </Box>

      {/* Staff List */}
      {staff.length === 0 ? (
        <Alert severity="info">
          Nu ai membri în echipă. Invită primul membru pentru a începe!
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {staff.map((member) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={member.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {member.firstName} {member.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, member.id)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Stack>

                  <Stack spacing={1} mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getRoleIcon(member.role)}
                      <Chip
                        label={member.role === 'shelter_admin' ? 'Administrator' : 'Staff'}
                        color={getRoleColor(member.role)}
                        size="small"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(member.status)}
                      <Chip
                        label={member.status === 'active' ? 'Activ' : 'Inactiv'}
                        color={getStatusColor(member.status)}
                        size="small"
                      />
                    </Box>
                    {member.phone && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneIcon fontSize="small" />
                        <Typography variant="body2">{member.phone}</Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Invite Staff Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invită Membru în Echipă</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Prenume"
              value={inviteForm.firstName}
              onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Nume"
              value={inviteForm.lastName}
              onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
              required
            />
            <TextField
              fullWidth
              select
              label="Rol"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
            >
              <MenuItem value="shelter_staff">Staff</MenuItem>
              <MenuItem value="shelter_admin">Administrator</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleInviteStaff}>
            Trimite Invitația
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Șterge Membru</DialogTitle>
        <DialogContent>
          <Typography>
            Ești sigur că vrei să ștergi acest membru din echipă? Această acțiune nu poate fi anulată.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => selectedStaff && handleDeleteStaff(selectedStaff.id)}
          >
            Șterge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const member = staff.find(s => s.id === menuStaffId);
          if (member) {
            setSelectedStaff(member);
            setEditDialogOpen(true);
          }
          handleMenuClose();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Editează</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          const member = staff.find(s => s.id === menuStaffId);
          if (member) {
            setSelectedStaff(member);
            setDeleteDialogOpen(true);
          }
          handleMenuClose();
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Șterge</ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ShelterStaffPage;
